import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CARD_STYLE = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  border: '1px solid #F1F3F5',
};

const LABEL_STYLE = { fontSize: 13, color: '#8B95A1', fontWeight: 500, marginBottom: 6 };
const VALUE_STYLE = { fontSize: 24, color: '#191F28', fontWeight: 700 };

const StatusBadge = ({ ok, label }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: ok ? '#E6F9EE' : '#FFF0F0',
      color: ok ? '#14AE5C' : '#F04452',
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: 999,
        background: ok ? '#14AE5C' : '#F04452',
      }}
    />
    {label}
  </span>
);

const Bar = ({ value, max, color = '#3182F6' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 8, background: '#F1F3F5', borderRadius: 999, overflow: 'hidden' }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 999,
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  );
};

const MonitoringPage = () => {
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [testing, setTesting] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [h, e] = await Promise.all([
        api.get('/health/deep').catch((err) => ({ data: null, _err: err })),
        api.get('/admin/errors?limit=100').catch((err) => ({ data: { errors: [] }, _err: err })),
      ]);
      setHealth(h.data);
      setErrors(e.data?.errors || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const testChannel = async (channel) => {
    setTesting(channel);
    try {
      const res = await api.post('/admin/notifications/test', { channel });
      const ok = res.data?.result?.ok;
      showToast(ok ? `${channel} 알림 전송 성공` : `전송 실패: ${res.data?.result?.error || '확인 필요'}`);
    } catch (err) {
      showToast(`전송 실패: ${err.response?.data?.error || err.message}`);
    } finally {
      setTesting(null);
    }
  };

  const checks = health?.checks || [];
  const metrics = health?.metrics || {};
  const overallOk = health?.status === 'healthy';

  // Bucketize errors by hour for tiny chart
  const now = Date.now();
  const buckets = new Array(12).fill(0);
  errors.forEach((err) => {
    const t = new Date(err.created_at).getTime();
    const hoursAgo = Math.floor((now - t) / 3600000);
    if (hoursAgo >= 0 && hoursAgo < 12) buckets[11 - hoursAgo]++;
  });
  const maxBucket = Math.max(1, ...buckets);

  return (
    <div style={{ padding: 32, background: '#F9FAFB', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#191F28', margin: 0 }}>시스템 모니터링</h1>
            <p style={{ fontSize: 14, color: '#8B95A1', margin: '4px 0 0' }}>
              {lastUpdated ? `마지막 업데이트: ${lastUpdated.toLocaleTimeString('ko-KR')}` : '불러오는 중...'}
            </p>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#3182F6',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            새로고침
          </button>
        </div>

        {/* Overall Status */}
        <div style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={LABEL_STYLE}>전체 시스템 상태</div>
              <div style={{ ...VALUE_STYLE, color: overallOk ? '#14AE5C' : '#F04452' }}>
                {loading ? '확인 중...' : overallOk ? '정상 운영 중' : '일부 서비스 장애'}
              </div>
            </div>
            <StatusBadge ok={overallOk} label={overallOk ? 'HEALTHY' : 'DEGRADED'} />
          </div>
        </div>

        {/* Checks grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 16 }}>
          {checks.map((check) => (
            <div key={check.name} style={CARD_STYLE}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={LABEL_STYLE}>
                  {check.name === 'database' ? '데이터베이스' : check.name === 'payment_gateway' ? '결제 게이트웨이' : check.name}
                </div>
                <StatusBadge ok={check.ok} label={check.ok ? 'OK' : 'DOWN'} />
              </div>
              <div style={{ fontSize: 13, color: '#4E5968' }}>
                응답 시간: <b>{check.latencyMs}ms</b>
              </div>
              {check.error && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#F04452', wordBreak: 'break-all' }}>{check.error}</div>
              )}
            </div>
          ))}
        </div>

        {/* Metrics grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div style={CARD_STYLE}>
            <div style={LABEL_STYLE}>최근 1시간 주문</div>
            <div style={VALUE_STYLE}>{metrics.ordersLastHour || 0}</div>
            <div style={{ marginTop: 12 }}>
              <Bar value={metrics.ordersLastHour || 0} max={100} color="#3182F6" />
            </div>
          </div>
          <div style={CARD_STYLE}>
            <div style={LABEL_STYLE}>최근 1시간 오류</div>
            <div style={{ ...VALUE_STYLE, color: (metrics.errorsLastHour || 0) > 0 ? '#F04452' : '#191F28' }}>
              {metrics.errorsLastHour || 0}
            </div>
            <div style={{ marginTop: 12 }}>
              <Bar value={metrics.errorsLastHour || 0} max={20} color="#F04452" />
            </div>
          </div>
          <div style={CARD_STYLE}>
            <div style={LABEL_STYLE}>오류율</div>
            <div style={VALUE_STYLE}>{((metrics.errorRate || 0) * 100).toFixed(2)}%</div>
            <div style={{ marginTop: 12 }}>
              <Bar value={(metrics.errorRate || 0) * 100} max={10} color="#FF9500" />
            </div>
          </div>
          <div style={CARD_STYLE}>
            <div style={LABEL_STYLE}>평균 응답시간</div>
            <div style={VALUE_STYLE}>{metrics.avgResponseMs != null ? `${metrics.avgResponseMs}ms` : '—'}</div>
            <div style={{ marginTop: 12 }}>
              <Bar value={metrics.avgResponseMs || 0} max={1000} color="#14AE5C" />
            </div>
          </div>
        </div>

        {/* Error chart */}
        <div style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <div style={LABEL_STYLE}>시간대별 오류 (최근 12시간)</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, marginTop: 16 }}>
            {buckets.map((v, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div
                  style={{
                    width: '100%',
                    height: `${(v / maxBucket) * 100}%`,
                    background: v > 0 ? '#F04452' : '#F1F3F5',
                    borderRadius: '6px 6px 0 0',
                    minHeight: 4,
                    transition: 'height 0.3s',
                  }}
                />
                <div style={{ fontSize: 11, color: '#8B95A1' }}>{12 - i}h</div>
              </div>
            ))}
          </div>
        </div>

        {/* Notification test */}
        <div style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#191F28', marginBottom: 4 }}>알림 테스트</div>
          <div style={{ fontSize: 13, color: '#8B95A1', marginBottom: 16 }}>설정된 채널로 테스트 알림을 발송합니다</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['email', 'slack', 'push', 'kakao'].map((ch) => (
              <button
                key={ch}
                disabled={testing === ch}
                onClick={() => testChannel(ch)}
                style={{
                  padding: '10px 18px',
                  background: testing === ch ? '#E5E8EB' : '#F2F4F6',
                  color: '#191F28',
                  border: '1px solid #E5E8EB',
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: testing === ch ? 'wait' : 'pointer',
                }}
              >
                {testing === ch ? '전송 중...' : `${ch === 'email' ? '이메일' : ch === 'slack' ? '슬랙' : ch === 'push' ? '푸시' : '카카오'} 테스트`}
              </button>
            ))}
          </div>
        </div>

        {/* Error logs */}
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#191F28' }}>오류 로그</div>
            <div style={{ fontSize: 13, color: '#8B95A1' }}>최근 {errors.length}건</div>
          </div>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            {errors.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#8B95A1', fontSize: 14 }}>오류가 없습니다</div>
            ) : (
              errors.map((err) => (
                <div
                  key={err.id}
                  style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #F1F3F5',
                    fontSize: 13,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 700,
                        background: err.level === 'error' || err.level === 'fatal' ? '#FFF0F0' : '#FFF9E6',
                        color: err.level === 'error' || err.level === 'fatal' ? '#F04452' : '#FF9500',
                      }}
                    >
                      {err.level?.toUpperCase()}
                    </span>
                    <span style={{ color: '#8B95A1', fontSize: 12 }}>
                      {new Date(err.created_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                  <div style={{ color: '#191F28', wordBreak: 'break-all' }}>{err.message}</div>
                  {err.url && <div style={{ color: '#8B95A1', fontSize: 12, marginTop: 2 }}>{err.url}</div>}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            background: '#191F28',
            color: '#fff',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
